// See https://uihubstg.wcm.collegeboard.org/ui/3.4.0/tables/ for Apricot Table
import { useQuery, NetworkStatus } from '@apollo/client'
import { GET_JOB_BY_DI_CODE } from '@landscape/gql'
import {
  CLIENT_TYPE,
  DataQualityFiles,
  OverlaySpinner,
  RefreshIcon,
  Table,
  appPreferencesFamily,
  dateDisplayFormat,
  fileNameParser,
  isDateRecentThan,
  jobStatus,
  jobStatusDescription,
  searchBarFocusState,
  useDashboardContext,
} from '@landscape/ui-lib'
import { selectUserDiCode } from '@landscape/ui-redux'
import { InstitutionInfo } from 'components/etc/InstitutionInfo'
import { setPageCode } from 'contextUtil'
import get from 'lodash/get'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { FileUploader } from '../file'
import { Message } from '../message'
import { JobFiles } from './JobFiles'

const jobsColumns = [
  {
    label: 'Job ID',
    field: 'id',
    sortable: false,
    justify: 'start',
    extraAttrs: {
      'data-testid': 'job-id-cell',
    },
  },
  {
    label: 'Status',
    field: 'status',
    sortable: false,
    justify: 'center',
    extraAttrs: {
      'data-testid': 'status-cell',
    },
  },
  {
    label: 'User',
    field: 'username',
    sortable: false,
    justify: 'center',
    extraAttrs: {
      'data-testid': 'user-cell',
    },
  },
  {
    label: 'Upload Date',
    field: 'date',
    sortable: false,
    justify: 'start',
    extraAttrs: {
      'data-testid': 'upload-date-cell',
    },
  },
  {
    label: 'Record Count',
    field: 'records',
    sortable: false,
    justify: 'end',
    extraAttrs: {
      'data-testid': 'record-count-cell',
    },
  },
  {
    label: 'File Download Link',
    field: 'fileName',
    addnClasses: 'cb-glyph cb-download',
    extraAttrs: {
      'aria-hidden': 'true',
      'data-testid': 'file-download-cell',
    },
    customComponent: { comp: JobFiles, props: {} },
    sortable: false,
    justify: 'start',
  },
  {
    label: 'Data Quality Report',
    field: 'dataQuality',
    addnClasses: 'cb-glyph cb-download',
    extraAttrs: {
      'aria-hidden': 'true',
      'data-testid': 'data-quality-report-cell',
    },
    customComponent: {
      comp: DataQualityFiles,
      props: { clientType: CLIENT_TYPE.EXTERNAL },
    },
    sortable: false,
    justify: 'center',
  },
]

const Jobs = (props: any) => {
  const setSearchBarFocus = useSetRecoilState(searchBarFocusState)
  const disable_job_upload = useRecoilValue(appPreferencesFamily('disable_job_upload'))
  const disable_job_upload_title = useRecoilValue(appPreferencesFamily('disable_job_upload_title'))
  const disable_job_upload_msg = useRecoilValue(appPreferencesFamily('disable_job_upload_msg'))
  const diCode = useSelector(selectUserDiCode)
  const dashboardReactContext = useDashboardContext()
  const [setUploadStatus] = useState({ errors: [] })

  const { data, loading, refetch, networkStatus } = useQuery(GET_JOB_BY_DI_CODE, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  })

  // We want to save the reference to refetch
  // so it can be called from the Modal/WsListener
  // when a job completes
  dashboardReactContext.refetchJobs = refetch
  
  const isLoading = loading || networkStatus === NetworkStatus.refetch

  useLayoutEffect(() => {
    document.title = 'Landscape - Jobs'
    setPageCode('jobs')
    // string equality check required bc all env vars are strings
    // if (import.meta.env.REACT_APP_DISABLE_UPLOAD === 'true') {
    //   setTimeout(() => {
    //     const browseButton = document.querySelector('.cb-file-button') as HTMLButtonElement
    //     browseButton.disabled = true
    //     // $('.cb-file-button').prop('disabled', true)
    //   }, 100)
    // }
  }, [])

  const refreshTable = (e: any) => {
    refetch()
  }

  useEffect(() => {
    // if (dashboardReactContext.refetchAppPreferences) {
    //   dashboardReactContext.refetchAppPreferences()
    // }
    setSearchBarFocus(false)
  }, [setSearchBarFocus])

  // TODO Take care of error case
  // if (loading) {
  //   // return <div>Loading...</div> //same as earlier need to make sure the useQuery has finished before trying to display the data from it.
  //   return <Spinner />

  // } else if (error) {
  //   // TODO Handle Error appropriately
  //   return <div>Error! {error.message}</div> //displays errors
  // }

  let jobs = get(data, 'readJobByDICodeQueryExternalAdmin.jobs') || []
  const jobsTableData: any = []

  jobs.forEach((job: any) => {
    // for failed & in-process jobs, only include if within 30 days
    if (job.status === jobStatus.FAILED && !isDateRecentThan(Date.parse(job.upload_date), 30)) {
      return
    }
    const { originalFileName } = fileNameParser(job.filename)
    jobsTableData.push({
      diCode,
      id: +job.id,
      status: jobStatusDescription(job.status),
      statusCode: job.status,
      qcStatusCode: job.qc_status,
      username: job.display_name || job.username,
      date: dateDisplayFormat(job.upload_date, { utc: true }),
      records: job.records,
      fileName: originalFileName,
    })
  })

  return (
    <div>
      <OverlaySpinner show={isLoading} id="jobs-spinner" />

      <InstitutionInfo />

      <div className="cb-band cb-band-panel" id="main-content" style={{ padding: '8px 0' }}>
        {disable_job_upload_msg && disable_job_upload_title && (
          <Message title={disable_job_upload_title} body={disable_job_upload_msg || ''} />
        )}
        <FileUploader setUploadStatus={setUploadStatus} disableButton={disable_job_upload} />
        <div className="cb-table-action-bar">
          <div className="cb-table-action-bar-btn">
            <button
              type="button"
              className="cb-btn cb-btn-circle"
              style={{ backgroundColor: 'white' }}
              aria-label="Refresh Table"
              onClick={refreshTable}
              data-testid="refresh-table-btn"
            >
              <RefreshIcon spin={loading} />
            </button>
          </div>
        </div>
      </div>

      {jobsTableData.length > 0 && (
        <>
          <Table
            id={`jobs-table`}
            data={jobsTableData}
            columns={jobsColumns}
            defaultSortField="id"
            defaultSortAsc={false}
            condensed
            pagination={jobsTableData.length > 25}
            paginationRowPerPage={25}
            uniqueKeyColumn="id"
            reduxTableName="completedJobsTable"
          />
        </>
      )}
    </div>
  )
}

export { Jobs }
